/**
 * Fabric Reinforcement Tool - BIM 2025 Grade
 * 
 * Creates Welded Wire Fabric (Mesh) reinforcement sheets in floors/walls.
 * Supports:
 * - Standard mesh types (e.g., 6x6-W1.4xW1.4)
 * - Sheet layout (Custom vs Standard formatting)
 * - Bend profiles (for bent fabric)
 * - Lap splice handling
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface FabricSheetType {
    name: string;
    majorSpacing: number; // mm
    minorSpacing: number; // mm
    majorWireDiameter: number; // mm
    minorWireDiameter: number; // mm
    sheetLength: number; // mm
    sheetWidth: number; // mm
    mass: number; // kg/m2
}

export interface FabricGeometryData {
    hostId: string;
    boundary: THREE.Vector3[];
    zOffset: number; // Cover offset
    majorDirection: THREE.Vector3;
    sheetType: string;
}

export interface FabricReinforcementElement extends Element {
    category: 'Structural Area Reinforcement'; // Mapping for Fabric
    geometryData: FabricGeometryData;
}

export class FabricReinforcementTool extends BaseTool {
    id = 'fabric_reinforcement';
    name = 'Fabric Reinforcement';
    cursor = 'crosshair';

    private hostId: string | null = null;
    private boundaryPoints: THREE.Vector3[] = [];

    // Default Sheet
    private currentSheetType: FabricSheetType = {
        name: '6x6-10/10',
        majorSpacing: 152,
        minorSpacing: 152,
        majorWireDiameter: 3.4,
        minorWireDiameter: 3.4,
        sheetLength: 4800,
        sheetWidth: 2400,
        mass: 1.5
    };

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // Auto-detect host (Floor or Wall) — manual scene raycast, element
        // meshes carry no R3F handlers.
        if (!this.hostId) {
            const hit = this.raycastSceneAtEvent(event).find(i => {
                const id = this.findId(i.object);
                if (!id) return false;
                const el = this.context!.bimStore.project.elements.get(id);
                return el && (el.category === 'Floors' || el.category === 'Walls' || el.category === 'Structural Foundations');
            });

            if (hit) {
                this.hostId = this.findId(hit.object)!;
                console.log(`Host selected: ${this.hostId}`);
            }
            return;
        }

        // Draw boundary on host face
        const pt = this.getIntersection(event);
        if (pt) {
            this.boundaryPoints.push(this.snapToGrid(pt));
            // Visual feedback handled in updatePreview

            if (this.boundaryPoints.length > 2) {
                const start = this.boundaryPoints[0];
                const last = this.boundaryPoints[this.boundaryPoints.length - 1];
                if (start.distanceTo(last) < 0.25) { // 25cm close tolerance (world meters)
                    this.boundaryPoints.pop();
                    this.createFabric();
                }
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const pt = this.getIntersection(event);
        if (pt && this.boundaryPoints.length > 0) {
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: [...this.boundaryPoints, pt],
                color: '#FF00FF' // Magenta for reinforcement
            });
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.boundaryPoints.length >= 3) {
            this.createFabric();
        } else if (event.key === 'Escape') {
            this.reset();
        }
    }

    private reset() {
        this.hostId = null;
        this.boundaryPoints = [];
        this.context?.appStore.setToolPreview(null);
    }

    private createFabric() {
        if (!this.context || !this.hostId) return;

        // Default Major Dir = X-axis of first line segment
        const p1 = this.boundaryPoints[0];
        const p2 = this.boundaryPoints[1];
        const majorDir = new THREE.Vector3().subVectors(p2, p1).normalize();

        const geometryData: FabricGeometryData = {
            hostId: this.hostId,
            boundary: this.boundaryPoints,
            zOffset: 25, // 25mm clear cover default
            majorDirection: majorDir,
            sheetType: this.currentSheetType.name
        };

        const fabric: FabricReinforcementElement = {
            id: uuidv4(),
            category: 'Structural Area Reinforcement',
            type: 'Fabric Sheet: ' + this.currentSheetType.name,
            parameters: {
                'Sheet Length': this.currentSheetType.sheetLength,
                'Sheet Width': this.currentSheetType.sheetWidth,
                'Major Spacing': this.currentSheetType.majorSpacing,
                'Wire Diameter': this.currentSheetType.majorWireDiameter
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(fabric);
        this.reset();
    }

    onPointerUp() { }
}
